// La confirmación que NO declara el modelo — el freno de todo lo que no se deshace.
//
// Vive en su propio módulo porque lo usan las cinco herramientas que escriben en la
// tienda del comerciante (traer, crear, reemplazar, cambiar datos, publicar) y, si viviera
// en una de ellas, importarlo desde las otras cerraría un círculo entre archivos.
//
// El mecanismo es el de `correr_flujo`
// (ADR-2026-09-17-flujos-el-permiso-para-gastar-no-lo-declara-el-modelo): al avisar se
// deja una marca con el número de turno, y la acción sólo corre si la persona habló
// DESPUÉS. La review del PR #127 mostró que eso no alcanzaba —hablar no es aceptar—, así
// que ahora además se mira QUÉ escribió
// (ADR-2026-09-21-la-confirmacion-mira-lo-que-contesto-la-persona).

/**
 * Los avisos dados, por acción y producto. Guardan en qué turno se avisó: si la persona
 * no habló desde entonces, la confirmación que manda el modelo no vale nada.
 */
const avisos = new Map<string, { clave: string; turno: number }>();

/** Para los tests: cada caso arranca sin avisos. */
export function olvidarAvisos(): void {
  avisos.clear();
}

const sinAcentos = (t: string): string =>
  t.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

/**
 * Lo que la persona escribió DESPUÉS del aviso, ¿es un sí?
 *
 * Que haya hablado no alcanza: si contestó "no" o preguntó otra cosa, el contador de
 * turnos sube igual y un `confirmado: true` posterior se colaba (review Greptile #127).
 * Acá se mira lo que escribió ELLA, no lo que el modelo dice que escribió.
 *
 * Dos reglas, en este orden:
 *  1. **Cualquier rastro de negación descalifica.** "sí, pero no toques las viejas" es un
 *     pedido distinto, no un permiso para éste.
 *  2. Después tiene que haber una afirmación. Si contestó otra cosa —una pregunta, una
 *     aclaración—, no se actúa: se vuelve a avisar, que es barato, en vez de tocar la
 *     tienda de alguien por las dudas.
 */
/**
 * Se compara SIN acentos, así que los patrones van sin acentos: "pará" llega como "para",
 * "hacé" como "hace". Ojo con eso: "para" NO está en la lista de negaciones justamente
 * porque es la preposición de todos los días ("subila para el producto"), y ahí negaría
 * un sí perfectamente válido.
 */
/**
 * Se compara SIN acentos, así que los patrones van sin acentos: "pará" llega como "para",
 * "hacé" como "hace". Ojo con eso: "para" NO está entre las negaciones justamente porque
 * es la preposición de todos los días ("subila para el producto"), y ahí negaría un sí
 * perfectamente válido.
 *
 * Y "si" NO está entre las afirmaciones: sin tilde es el condicional, que aparece en
 * preguntas como "¿qué pasa si me arrepiento?" o "si querés lo vemos mañana". El sí que
 * cuenta es el de la tilde, o un "si" que sea TODO el mensaje.
 */
const NIEGA =
  /(^|\s)(no|nop|nel|negativo|tampoco|nunca|nada|cancela|cancelalo|cancelala|cancelar|pare|frena|freno|olvidalo|olvidate|dejalo|deja|espera|esperemos|mejor no|todavia no|aun no|ahora no)(\s|$|[,.!?])/;
const AFIRMA =
  /(^|\s)(sip|sipi|claro|dale|dele|ok|oka|okey|okay|listo|hacelo|hacela|hace|haga|hagamos|hagamoslo|adelante|confirmo|confirmado|confirma|obvio|de una|perfecto|correcto|exacto|mandale|manda|mandalo|subila|subilo|subilas|subi|crealo|creala|crea|publicalo|publicala|publica|reemplazalo|reemplazala|reemplaza|cambialo|cambiala|cambia|vale|bueno|de acuerdo|por supuesto|procede|proceder)(\s|$|[,.!?])/;

/**
 * Saca las preguntas: lo que va entre "¿" y "?" (o hasta el final, si falta el cierre) y
 * cualquier oración que termine en "?".
 */
function sinPreguntas(t: string): string {
  return t
    .replace(/¿[^?]*(\?|$)/g, ' ')
    .replace(/[^.!?¿]*\?/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Un "sí" con tilde en cualquier lado, o un "si" que sea el mensaje entero. */
const SI_SOLO = /^s[ií][\s.!,]*$/;
const SI_CON_TILDE = /(^|\s)sí(\s|$|[,.!?])/;

export function esRespuestaAfirmativa(mensaje: string): boolean {
  // Una PREGUNTA no es un permiso, aunque tenga adentro una palabra del sí: "¿qué cambia?" o
  // "¿lo publica solo?" daban verdadero por "cambia" y "publica". Se sacan las preguntas y se
  // mira lo que queda: "sí, dale. ¿Y después?" sigue siendo un sí; "¿qué cambia?" ya no.
  const completo = mensaje.toLowerCase().replace(/\s+/g, ' ').trim();
  // La negación gana siempre, también adentro de una pregunta: "sí, pero no borres las viejas"
  // es otro pedido, y "dale, ¿o mejor no?" es una duda, no un permiso.
  if (NIEGA.test(` ${sinAcentos(completo).replace(/[¿?]/g, ' ')} `)) return false;
  const limpio = sinPreguntas(completo);
  if (!limpio) return false;
  const sinAc = ` ${sinAcentos(limpio)} `;
  if (SI_SOLO.test(limpio) || SI_CON_TILDE.test(limpio)) return true;
  return AFIRMA.test(sinAc);
}

/**
 * ¿Se puede actuar? Sólo si (a) el modelo dice que confirmó, (b) hay un aviso previo para
 * EXACTAMENTE esto, (c) la persona habló después de ese aviso y (d) **lo que escribió es
 * un sí**.
 *
 * La (d) es lo que separa "habló" de "aceptó". Sin ella, alcanzaba con que la conversación
 * siguiera —aunque hubiera dicho que no— para que una llamada posterior con `confirmado`
 * pisara fotos o cambiara precios (review Greptile #127).
 *
 * Sin turno conocido no se actúa: preferimos un aviso repetido a un borrado que nadie
 * pidió.
 */
export function puedeActuar(args: {
  accion: string;
  clave: string;
  confirmado: boolean;
  turno: number | null;
  /** El último mensaje de la PERSONA. Vacío o ausente = no se actúa. */
  ultimoMensaje?: string;
}): boolean {
  if (!args.confirmado || args.turno === null) return false;
  const previo = avisos.get(args.accion);
  if (!previo || previo.clave !== args.clave || args.turno <= previo.turno) return false;
  return esRespuestaAfirmativa(args.ultimoMensaje ?? '');
}

/**
 * El aviso ya se usó: se borra para que el MISMO permiso no habilite una segunda acción
 * idéntica más adelante sin volver a preguntar.
 */
export function consumirAviso(accion: string): void {
  avisos.delete(accion);
}

/** Deja la marca de que se avisó, para que la respuesta de la persona la habilite. */
export function anotarAviso(accion: string, clave: string, turno: number | null): void {
  avisos.set(accion, { clave, turno: turno ?? 0 });
}

